import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { saveAs } from 'file-saver';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { v1URL } from '../../../../../../../shared/constants/urls.constants';

/**
 * MGVW-record HTTP service — the web client for the api's /v1/mgvw-record
 * router (contract-level MGVW agency view, Alaska DOT). Same shape as
 * SampleRecordsService: JSON through CommonService.callAPI (token header,
 * body unwrapped), binaries through AppHttpService so the blob and the
 * server's Content-Disposition filename come back together.
 */
@Injectable({
  providedIn: 'root',
})
export class MgvwRecordsService {
  /** Fired by the side panel after save / submit / delete so the list refetches. */
  refresh: Subject<void> = new Subject<void>();

  constructor(
    private commonService: CommonService,
    private appHttpService: AppHttpService,
  ) {}

  /** Every record on the project, newest first. `projectId` may be the uuid or numeric id. */
  list(projectId: string): Observable<any> {
    const cfg = v1URL.mgvwRecord.list;
    return this.commonService.callAPI(cfg.method, cfg.url({ projectId }));
  }

  /**
   * `uuid` is the client-generated idempotency key: replaying the same POST
   * updates the row it created instead of duplicating it.
   */
  create(payload: { project_uuid: string; values: any; uuid?: string }): Observable<any> {
    const cfg = v1URL.mgvwRecord.create;
    return this.commonService.callAPI(cfg.method, cfg.url(), payload);
  }

  update(uuid: string, values: any): Observable<any> {
    const cfg = v1URL.mgvwRecord.update;
    return this.commonService.callAPI(cfg.method, cfg.url({ uuid }), { values });
  }

  delete(uuid: string): Observable<any> {
    const cfg = v1URL.mgvwRecord.delete;
    return this.commonService.callAPI(cfg.method, cfg.url({ uuid }));
  }

  /** Pushes the record to AASHTOWare; the api flips it to `submitted` (read-only) on success. */
  submit(uuid: string): Observable<any> {
    const cfg = v1URL.mgvwRecord.submit;
    return this.commonService.callAPI(cfg.method, cfg.url({ uuid }), {});
  }

  /** The DOT GVW worksheet PDF for one record. */
  downloadPdf(uuid: string): Promise<void> {
    return this.downloadFile(v1URL.mgvwRecord.pdf.path({ uuid }), `mgvw-${uuid}.pdf`);
  }

  /** The "MGVW Report" spreadsheet for every record on the project. */
  downloadXls(projectId: string, projectName?: string): Promise<void> {
    const safeName = (projectName || 'project').replace(/[\\/]/g, '_');
    return this.downloadFile(v1URL.mgvwRecord.xls.path({ projectId }), `MGVW Report - ${safeName}.xlsx`);
  }

  private downloadFile(path: string, fallbackName: string): Promise<void> {
    return this.appHttpService
      .getService(path, {}, { responseType: 'blob' as 'json', observe: 'response' })
      .toPromise()
      .then(async (resp: any) => {
        if (resp?.status === 200 && resp.body instanceof Blob) {
          let name = fallbackName;
          const disposition = resp.headers?.get('Content-Disposition');
          const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match?.[1]) name = match[1].replace(/['"]/g, '');
          saveAs(resp.body, name);
          return;
        }
        // A non-200 body is the api's JSON error, delivered as a blob.
        const text = resp?.body instanceof Blob ? await resp.body.text() : '';
        let message = 'Download failed';
        try {
          message = JSON.parse(text)?.message || message;
        } catch {
          // keep the default
        }
        throw new Error(message);
      });
  }
}
